import React, { useCallback } from 'react';
import { ModalType } from '../types';
import heroBg from '../assets/hero-bg.svg'; // ← move hero-bg.svg into src/assets/

interface HeroProps {
  onOpenModal: (type: ModalType) => void;
}

const Hero: React.FC<HeroProps> = () => {
  const scrollToWork = useCallback(() => {
    document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        paddingTop: 120,
        paddingBottom: 80,
        paddingLeft: 'clamp(24px, 6vw, 80px)',
        paddingRight: 'clamp(24px, 6vw, 80px)',
        background: 'linear-gradient(158deg, #050504 0%, #06080e 45%, #070a0d 100%)',
      }}
    >
      {/* ── SVG background illustration ── */}
      <img
        src={heroBg}
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />

      {/* ── Decorative vertical lines ── */}
      <div
        className="absolute top-0 pointer-events-none"
        style={{
          right: '26%',
          width: 1,
          height: '42%',
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(184,154,90,0.28) 50%, transparent 100%)',
        }}
      />
      <div
        className="absolute bottom-0 pointer-events-none"
        style={{
          right: '40%',
          width: 1,
          height: '28%',
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(26,168,138,0.18) 50%, transparent 100%)',
        }}
      />

      {/* ── Main content ── */}
      <div className="relative z-10" style={{ maxWidth: '54rem' }}>
        {/* Eyebrow */}
        <div
          className="flex items-center mb-8"
          style={{
            gap: 14,
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: '#b89a5a',
          }}
        >
          <span
            style={{
              width: 38,
              height: 1,
              background: 'linear-gradient(to right, #b89a5a, #24a88a)',
              display: 'inline-block',
            }}
          />
          Strategic Digital Growth
          <span
            style={{
              width: 1,
              height: 11,
              background: 'rgba(184,154,90,0.35)',
              display: 'inline-block',
            }}
          />
          <span style={{ color: '#1a8878' }}>Est. 2021</span>
        </div>

        {/* Headline */}
        <h1
          className="mb-2 leading-[1.04]"
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 'clamp(44px, 6.5vw, 84px)',
            fontWeight: 300,
            color: '#f2ece0',
            letterSpacing: '-0.01em',
          }}
        >
          The System
          <br />
          Behind{' '}
          <em style={{ fontStyle: 'italic', color: '#b89a5a' }}>Serious</em>
          <br />
          Brand Growth.
        </h1>

        {/* Subheadline */}
        <p
          className="mb-8"
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 'clamp(22px, 2.8vw, 36px)',
            fontWeight: 300,
            fontStyle: 'italic',
            color: '#78736c',
            lineHeight: 1.2,
          }}
        >
          Not content. Not campaigns.{' '}
          <span style={{ color: '#1a7a68' }}>Structure.</span>
        </p>

        {/* Body */}
        <p
          className="mb-12 max-w-lg"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 13,
            fontWeight: 300,
            color: '#aea89f',
            lineHeight: 2,
            letterSpacing: '0.02em',
          }}
        >
          Most brands don't have a marketing problem. They have a structure problem.
          HNL Digital Luxe builds the systems that connect visibility to revenue —
          for hospitality, fashion, beauty, real estate, and premium consumer brands
          that are ready to grow with precision.
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap items-center mb-14" style={{ gap: 16 }}>
          <button
            onClick={scrollToWork}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              background: '#b89a5a',
              color: '#0d0c0b',
              padding: '15px 34px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#d4b87a';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 28px rgba(184,154,90,0.22)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#b89a5a';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            View Selected Work
          </button>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div
        className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center z-10"
        style={{
          bottom: 30,
          gap: 10,
          fontFamily: 'Montserrat, sans-serif',
          fontSize: 8,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: '#4a4640',
        }}
      >
        Scroll
        <span className="scroll-line-el" />
      </div>
    </section>
  );
};

export default Hero;